import { z } from 'zod';
import { EpochMsSchema, SlugSchema, UlidSchema } from './common';

export const GroupVisibilitySchema = z.enum(['public', 'private']);
export const GROUP_ROLES = ['owner', 'moderator', 'member'] as const;
export const GroupRoleSchema = z.enum(GROUP_ROLES);
export const GROUP_MEMBER_STATUSES = ['active', 'pending'] as const;
export const GroupMemberStatusSchema = z.enum(GROUP_MEMBER_STATUSES);

export const GroupCreateSchema = z.object({
  name: z.string().min(2).max(120),
  description: z.string().max(2000).default(''),
  visibility: GroupVisibilitySchema.default('public'),
  orgId: UlidSchema.nullish(),
});

export const GroupPatchSchema = z
  .object({
    name: z.string().min(2).max(120),
    description: z.string().max(2000),
    visibility: GroupVisibilitySchema,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided');

export const GroupSchema = z.object({
  id: UlidSchema,
  slug: SlugSchema,
  name: z.string(),
  description: z.string(),
  visibility: GroupVisibilitySchema,
  orgId: UlidSchema.nullable(),
  ownerUserId: UlidSchema,
  memberCount: z.number().int().nonnegative(),
  seoEnabled: z.boolean(),
  geoEnabled: z.boolean(),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
  /** Present only for the caller: their membership state, if any. */
  viewerStatus: z.enum(['active', 'pending', 'none']).optional(),
  viewerRole: GroupRoleSchema.nullable().optional(),
});
export const GroupListSchema = z.object({ items: z.array(GroupSchema), nextCursor: z.string().nullable() });
export const GroupIdParamsSchema = z.object({ id: UlidSchema });
export const GroupListQuerySchema = z.object({
  q: z.string().max(120).optional(),
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const GroupMemberSchema = z.object({
  userId: UlidSchema,
  /**
   * Display name; null only for deleted accounts. A member whose profile the viewer may not see
   * (shared visibility rule) is shown as the placeholder "flory.sh member".
   */
  name: z.string().nullable(),
  /** Profile handle; null unless the viewer may see the member's profile. */
  handle: z.string().nullable(),
  role: GroupRoleSchema,
  status: GroupMemberStatusSchema,
  createdAt: EpochMsSchema,
});
export const GroupMembersSchema = z.object({ items: z.array(GroupMemberSchema) });
export const GroupMemberParamsSchema = z.object({ id: UlidSchema, userId: UlidSchema });
export const GroupMemberRoleInputSchema = z.object({ role: GroupRoleSchema });

export const GroupPostInputSchema = z.object({ body: z.string().min(1).max(5000) });
export const GroupPostSchema = z.object({
  id: UlidSchema,
  groupId: UlidSchema,
  authorId: UlidSchema,
  body: z.string(),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
});
export const GroupPostListSchema = z.object({
  items: z.array(GroupPostSchema),
  nextCursor: z.string().nullable(),
});
export const GroupPostParamsSchema = z.object({ id: UlidSchema, postId: UlidSchema });
